import type { Interview, InterviewAnswer, Question } from '~/types'
import { useRouteParams } from '@vueuse/router'

/**
 * @file This store is responsible for managing the room or interview related states and actions.
 *
 * @todo
 * - [x] Add a way to start the interview
 * - [ ] Add next question button
 * - [ ] Add a way to submit/finish the interview
 * - [ ] Add a way to cancel the interview
 */

export const useRoomStore = defineStore('room', () => {
  // STATES
  const room = ref<Interview>()
  const pending = ref(false)
  const token = ref(useRouteParams<string>('token')) // token id from the url
  // interview questions related
  const questions = computed<Question[]>(() => room.value?.jobPost?.questions ?? [])
  const answers = computed<InterviewAnswer[]>(() => room.value?.interviewAnswers ?? [])
  const answeredQuestions = computed<Question[]>(() => questions.value.filter(question => answers.value.find(answer => answer.questionId === question.questionId)))
  const unansweredQuestions = computed<Question[]>(() => questions.value.filter(question => !answers.value.find(answer => answer.questionId === question.questionId)))
  const currentQuestion = computed<Question>(() => unansweredQuestions.value[0] as Question)
  const recordingDuration = ref(0)

  // ACTIONS
  async function getRoom() {
    if (!token.value)
      return

    const response = await $fetchApi<{ data: Interview }>(`/public/interviews/${token.value}`, {
      transform: data => useCase().toCamelCase(data.data),
      onRequest: () => pending.value = true,
    })

    room.value = response.data.value as unknown as Interview
    pending.value = false

    return response
  }

  async function submitAnswer(answer: InterviewAnswer) {
    if (!token.value)
      return

    const response = await $fetchApi<{ data: Interview }>(`/public/interview_room/${token.value}/interview_answers`, {
      method: 'POST',
      params: answer,
      transform: data => useCase().toCamelCase(data.data),
      onRequest: () => pending.value = true,
    })

    room.value = response.data.value as unknown as Interview
    pending.value = false

    return response
  }

  async function updateRoom(params?: Interview) {
    if (!token.value)
      return

    const response = await $fetchApi<{ data: Interview }>(`/public/interviews/${token.value}`, {
      method: 'PATCH',
      params,
      transform: (data: { data: Interview }) => useCase().toCamelCase(data.data),
      onRequest: () => pending.value = true,
    })

    room.value = response.data.value as unknown as Interview
    pending.value = false

    return response
  }

  return {
    state: computed(() => room.value?.status),
    jobPost: computed(() => room.value!.jobPost),
    pending,
    token,
    room,
    questions,
    answers,
    answeredQuestions,
    unansweredQuestions,
    currentQuestion,
    recordingDuration,
    getRoom,
    updateRoom,
    submitAnswer,
  }
})
